"use client";

import { isMacOS } from "@pharaday/utils";
import { cva, type VariantProps } from "class-variance-authority";
import { type HTMLAttributes, type Ref, useSyncExternalStore } from "react";

import { cn } from "../../utils/cn.util";

export const shortcutVariants = cva(
  "inline-flex shrink-0 items-center text-xs text-neutral-50 group-aria-disabled:text-neutral-90",
  {
    variants: {
      variant: {
        // One key cap per key, e.g. next to a Button's label.
        keycap: "gap-1 font-medium",
        // Plain text, e.g. trailing a dropdown menu item's label.
        text: "leading-4.5 font-normal whitespace-nowrap",
      },
    },
    defaultVariants: {
      variant: "keycap",
    },
  }
);

export interface ShortcutKey {
  key: string;
  /** Command (Mac) or Ctrl (Windows). */
  ctrl?: boolean;
  /** Option (Mac) or Alt (Windows). */
  alt?: boolean;
  shift?: boolean;
}

export type ShortcutProps = Omit<HTMLAttributes<HTMLSpanElement>, "children"> &
  VariantProps<typeof shortcutVariants> & {
    ref?: Ref<HTMLSpanElement>;
    /** The key to press, alone (`"e"`) or with modifiers (`{ key: "k", ctrl: true }`). */
    mainKey: string | ShortcutKey;
    /** A key to press next, for two-step shortcuts. */
    subKey?: string;
  };

// The OS never changes, so there is nothing to subscribe to.
const subscribe = () => () => undefined;

/** The server can't know the OS: it renders Windows names, which the client corrects without hiding anything. */
const useIsMac = () =>
  useSyncExternalStore(
    subscribe,
    () => isMacOS,
    () => false
  );

const Kbd = ({ children }: { children: string }) => (
  <kbd className="inline-flex h-5 min-w-5 items-center justify-center rounded border border-neutral-90 bg-neutral-99 px-1 font-sans leading-none">
    {children}
  </kbd>
);

const Shortcut = ({ ref, className, variant, mainKey, subKey, ...props }: ShortcutProps) => {
  const isMac = useIsMac();
  const { key, ctrl, alt, shift } = typeof mainKey === "string" ? { key: mainKey } : mainKey;
  const keys = [ctrl && (isMac ? "⌘" : "Ctrl"), alt && (isMac ? "⌥" : "Alt"), shift && "⇧", key.toUpperCase()].filter(
    (value): value is string => Boolean(value)
  );

  return (
    <span ref={ref} className={cn(shortcutVariants({ variant }), className)} {...props}>
      {variant === "text" ? (
        <>
          {keys.join(isMac ? "" : "+")}
          {subKey && `→${subKey.toUpperCase()}`}
        </>
      ) : (
        <>
          {keys.map(value => (
            <Kbd key={value}>{value}</Kbd>
          ))}
          {subKey && (
            <>
              then
              <Kbd>{subKey.toUpperCase()}</Kbd>
            </>
          )}
        </>
      )}
    </span>
  );
};

Shortcut.displayName = "Shortcut";

export default Shortcut;
